import { defineComponent, ref, computed, watch, type CSSProperties, unref, Teleport } from 'vue'
import type { IBubbletipProps } from './types'
import { getScopedClass } from '@/utils/component'
import { useElementBounding } from '@vueuse/core'
import { tv } from 'tailwind-variants'
import { unwrapElementRef } from '@/utils/dom/unwrap'
import { VEC2, Vector } from '@/utils/math/vector'
import { lerp, smoothstep } from '@/utils/math'
import { svgCommandPath } from '@/utils/svg'
import { svgPathLineTo } from '@/utils/svg/command'
import { VEC3, clamp } from 'math'
import { twColor } from '@/utils/style'
import { useTooltip } from './hooks/useTooltip'

const clazz = tv({
  slots: {
    base: `text-white bg-primary-500 p-4 font-semibold text-sm rounded-lg`,
    tip: 'bg-primary-500'
  },
  variants: {
    color: {
      white: {
        base: 'bg-white text-gray-900',
        tip: 'bg-white text-gray-900'
      },
      blue800: {
        base: 'bg-blue-800 text-white',
        tip: 'bg-blue-800 text-white'
      },
      findex800: {
        base: 'bg-findex-800 text-white',
        tip: 'bg-findex-800 text-white'
      },
      primary700: {
        base: 'bg-primary-700 text-white',
        tip: 'bg-primary-700 text-white'
      },
      primary100: {
        base: 'bg-primary-100 text-white',
        tip: 'bg-primary-100 text-white'
      },
      primary5: {
        base: 'bg-primary-400 text-white',
        tip: 'bg-primary-400 text-white'
      },
      warning: {
        base: 'bg-amber-400 text-white',
        tip: 'bg-amber-400 text-white'
      }
    },
    size: {
      s: {
        base: 'text-xs p-2'
      }
    },
    noPadding: {
      true: {
        base: 'p-0'
      }
    }
  }
})

const Bubbletip = defineComponent<IBubbletipProps>({
  setup(props) {
    const toolref = ref()
    const handler = props.hook || useTooltip(toolref, props)

    const triggerBound = useElementBounding(props.triggerRef)
    const toolBound = useElementBounding(toolref)
    const scopedClass = getScopedClass(true) + Math.floor(Math.random() * 100)
    const tipSize = computed((): Vector => {
      const s = props.size === 's' ? 9 : 16
      return VEC2(s, s)
    });

    const shadowOffset = computed(() => {
      const offsetScale = 0.2;
      const f = handler.anim.current.value;
      return 0.1 * f + lerp(0.0, 0.5, f * f) * offsetScale
    })

    const shadowBlur = computed(() => {
      const blurScale = 0.2
      const f = handler.anim.current.value
      return lerp(0.05, 0.5, f * f * f) * blurScale
    })

    const shadowAlpha = computed(() => {
      const f = handler.anim.current.value
      return f * f * 0.15
    })

    const animatedStyle = computed((): CSSProperties => {
      return {
        left: handler.pos.value.x + 'px',
        top: handler.pos.value.y + 'px',
        opacity: `${handler.anim.current.value * 100}%`
        //filter: `drop-shadow(0px ${shadowOffset}rem ${shadowBlur}rem rgba(0, 0, 0, 0.15))`
      }
    })

    const { base: baseClass, tip: tipClass } = clazz({
      color: props.variant as any,
      size: props.size as any,
      noPadding: props.noPadding || false
    })

    const color = twColor(tipClass({}))

    const tipStyle = computed((): CSSProperties => {
      const p = handler.tipPos.value
      const f = handler.anim.current.value * handler.anim.current.value
      const opacity = lerp(0, 100, smoothstep(0.5, 1.0, f))
      const scale = 1;//f / (1.0 + Math.max(0.0, -handler.animDir.value))

      const cf = (x: number) => x * x

      const getColor = () => {
        if (!props.tipAppearance?.colorStops) return color
        const stops = handler.tipStops.value
        const baseColor = 'rgb(255, 255, 255)'
        const colLeft = Vector.fromColor(props.tipAppearance?.colorStops?.left || baseColor)
        const colRight = Vector.fromColor(props.tipAppearance?.colorStops?.right || baseColor)
        const colTop = Vector.fromColor(props.tipAppearance?.colorStops?.top || baseColor)
        const colBottom = Vector.fromColor(props.tipAppearance?.colorStops?.bottom || baseColor)
        const col = VEC3(255, 255, 255)
          .lerp(colLeft, cf(stops.x))
          .lerp(colRight, cf(stops.y))
          .lerp(colTop, cf(stops.z))
          .lerp(colBottom, cf(stops.w))
        return col.toRGB(6)
      }

      const colstr = getColor()
      const rot = 45

      return {
        width: tipSize.value.x + 'px',
        height: tipSize.value.y + 'px',
        position: 'fixed',
        transform: `rotate(${rot}deg) scale(${scale}, ${scale})`,
        left: p.x + 'px',
        top: p.y + 'px',
        opacity: `${opacity}%`,
        zIndex: (props.zIndex ?? 600) - 10,
        background: colstr,
        //filter: `drop-shadow(4px 2px 1px rgba(0, 0, 0, 0.15))`,
        pointerEvents: 'none',
        ...(props.placement === 'mouse'
          ? {
            display: 'none',
            opacity: `0%`
          }
          : {})
      }
    })

    const tieTip = computed(() => {
      const target = unref(props.targetPosition);
      if (!target || unref(props.disabled)) return <div />

      const toolEl = unwrapElementRef(toolref)
      if (!toolEl) return <div />
      const toolRect = toolEl.getBoundingClientRect()

      const p = handler.pos.value

      const svgSize = VEC2(window.innerWidth, window.innerHeight) //VEC2(toolRect.width, toolRect.height).scale(4);
      const tsize = VEC2(toolRect.width, toolRect.height)
      const posGlobal = VEC2(
        0.5 * tsize.x + p.x - 0.5 * svgSize.x,
        0.5 * tsize.y + p.y - 0.5 * svgSize.y
      )

      const center = handler.pos.value.add(VEC2(toolRect.width * 0.5, toolRect.height * 0.5))
      const dir = target.sub(center).unit()
      const svgCenter = svgSize.scale(0.5)
      const wide = 0.75

      const arrow = (a: Vector, b: Vector) => {
        const sep = a.sub(b)
        const len = sep.mag()
        const di = sep.unit()

        const headLen = clamp(Math.sqrt(len), 12, len / Math.PI) + 10

        return [
          svgPathLineTo({
            moveTo: a,
            point: b.add(di.rotate(wide, VEC3(0, 0, 1)).scale(0.5 * headLen))
          }),
          svgPathLineTo({ point: b }),
          svgPathLineTo({ point: b.add(di.rotate(-wide, VEC3(0, 0, 1)).scale(0.5 * headLen)) }),
          svgPathLineTo({ point: a })
          //svgPathLineTo({ moveTo: b, point: b.add(di.rotate(-0.4, VEC3(0, 0, 1)).scale(headLen)) })
        ]
      }

      const dist = center.distance(target)
      const tips = Math.max(tipSize.value.x, tipSize.value.y)
      const dsize = Math.min(0.5 * tsize.mag(), dist + tips)
      const end = svgCenter.add(dir.scale(dsize + tips))
      const arrowCommands = arrow(svgCenter, end)

      const cf = (x: number) => x * x
      const getColor = () => {
        if (!props.tipAppearance?.colorStops) return color
        const stops = handler.tipStops.value
        const baseColor = 'rgb(255, 255, 255)'
        const colLeft = Vector.fromColor(props.tipAppearance?.colorStops?.left || baseColor)
        const colRight = Vector.fromColor(props.tipAppearance?.colorStops?.right || baseColor)
        const colTop = Vector.fromColor(props.tipAppearance?.colorStops?.top || baseColor)
        const colBottom = Vector.fromColor(props.tipAppearance?.colorStops?.bottom || baseColor)
        const col = VEC3(255, 255, 255)
          .lerp(colLeft, cf(stops.x))
          .lerp(colRight, cf(stops.y))
          .lerp(colTop, cf(stops.z))
          .lerp(colBottom, cf(stops.w))
        return col.toRGB(6)
      }

      const colstr = getColor()
      const f = handler.anim.current.value
      const opacity = f * f

      return (
        <svg
          width={svgSize.x + 'px'}
          height={svgSize.y + 'px'}
          style={{
            position: 'fixed',
            top: posGlobal.y,
            left: posGlobal.x,
            pointerEvents: 'none',
            zIndex: (props.zIndex ?? 600) - 10
          }}
        >
          <defs>
            <filter id="f3" width="120" height="120">
              <feOffset in="SourceAlpha" dx="4" dy="4" />
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.33" />
              </feComponentTransfer>
              <feGaussianBlur stdDeviation="4" />
              <feBlend in="SourceGraphic" in2="blurOut" />
            </filter>
          </defs>
          {svgCommandPath({
            commands: [...arrowCommands],
            opacity: `${opacity * 100}%`,
            fill: colstr,
            filter: 'url(#f3)'
          })}
        </svg>
      )
    })

    const arrowTip = computed(() => {
      const target = unref(props.targetPosition)
      if (!target) return <div />

      const toolEl = unwrapElementRef(toolref)
      if (!toolEl) return <div />
      const toolRect = toolEl.getBoundingClientRect()

      const p = handler.pos.value

      const svgSize = VEC2(window.innerWidth, window.innerHeight) //VEC2(toolRect.width, toolRect.height).scale(4);
      const tsize = VEC2(toolRect.width, toolRect.height)
      const posGlobal = VEC2(
        0.5 * tsize.x + p.x - 0.5 * svgSize.x,
        0.5 * tsize.y + p.y - 0.5 * svgSize.y
      )

      const center = handler.pos.value.add(VEC2(toolRect.width * 0.5, toolRect.height * 0.5))
      const dir = target.sub(center).unit()
      const svgCenter = svgSize.scale(0.5)

      const dist = center.distance(target)

      const arrow = (a: Vector, b: Vector) => {
        const sep = a.sub(b)
        const len = sep.mag()
        const di = sep.unit()

        const headLen = clamp(Math.sqrt(len), 12, len / Math.PI)

        return [
          svgPathLineTo({ moveTo: a, point: b }),
          svgPathLineTo({ moveTo: b, point: b.add(di.rotate(0.4, VEC3(0, 0, 1)).scale(headLen)) }),
          svgPathLineTo({ moveTo: b, point: b.add(di.rotate(-0.4, VEC3(0, 0, 1)).scale(headLen)) })
        ]
      }

      return (
        <svg
          width={svgSize.x + 'px'}
          height={svgSize.y + 'px'}
          style={{
            position: 'fixed',
            top: posGlobal.y,
            left: posGlobal.x,
            pointerEvents: 'none',
            zIndex: (props.zIndex ?? 600) - 10
          }}
        >
          <defs>
            <filter id="f3" width="120" height="120">
              <feOffset in="SourceAlpha" dx="4" dy="4" />
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.33" />
              </feComponentTransfer>
              <feGaussianBlur stdDeviation="4" />
              <feBlend in="SourceGraphic" in2="blurOut" />
            </filter>
          </defs>
          {svgCommandPath({
            commands: [...arrow(svgCenter, svgCenter.add(dir.scale(dist)))],
            opacity: `${handler.anim.current.value * 100}%`,
            stroke: color,
            'stroke-width': '2px',
            filter: 'url(#f3)'
          })}
        </svg>
      )
    })

    const bodyComp = props.body

    // Lazy-mount: only render the body component once the tooltip first
    // becomes visible. Stays mounted after that so re-hover is instant.
    // The `eager` prop is an escape hatch for callers whose body has
    // animation/state that must exist from initial render. (DEV-4500)
    //
    // Seed from the current anim value to handle the rare case where the
    // tooltip starts already open (e.g. visible/active set on first render).
    // This avoids the TDZ trap of a `{ immediate: true }` watch whose
    // callback would close over the not-yet-assigned `stop` handle.
    const hasOpened = ref(
      props.eager === true || handler.anim.current.value > 0.00001,
    )
    if (!hasOpened.value) {
      const stop = watch(
        () => handler.anim.current.value,
        (v) => {
          if (v > 0.00001) {
            hasOpened.value = true
            stop()
          }
        },
      )
    }

    const updateBounds = () => {
      triggerBound.update()
      toolBound.update()
    }

    return () => {
      return (
        <Teleport to="body">
          <div
            style={[
              props.style || {},
              {
                position: 'relative',
                zIndex: (props.zIndex ?? 600) - 2,
                ...(handler.anim.current.value <= 0.00001
                  ? {
                    display: 'none'
                  }
                  : {})
              }
            ]}
          >
            <div
              style={{
                position: 'fixed',
                width: '100vw',
                height: '100vh',
                filter: `drop-shadow(2px ${shadowOffset.value}rem ${shadowBlur.value}rem rgba(0, 0, 0, ${shadowAlpha.value}))`,
                top: 0,
                left: 0,
                pointerEvents: 'none'
              }}
            >
              <div
                {...{ onVnodeMounted: updateBounds, onVnodeUpdated: updateBounds }}
                ref={toolref}
                class={[baseClass({}), scopedClass]}
                style={[
                  {
                    display: 'flex',
                    width: 'auto',
                    content: '',
                    minWidth: '1rem',
                    minHeight: '1rem',
                    ...(props.wrapText === true ? {} : {
                      whiteSpace: 'nowrap',
                    }),
                    height: 'auto',
                    position: 'fixed',
                    zIndex: props.zIndex ?? 600,
                    pointerEvents: props.hoverable ? 'all' : 'none',
                    ...(unref(props.disabled) || handler.anim.current.value <= 0.00001
                      ? {
                        display: 'none'
                      }
                      : {})
                  },
                  animatedStyle.value
                ]}
              >
                <div
                  style={{
                    pointerEvents: 'none',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {props.text ? props.text : bodyComp && hasOpened.value ? <bodyComp /> : ''}
                </div>
              </div>

              {props.tipAppearance?.disabled === true ? null : props.tipAppearance?.shape ===
                'arrow' ? (
                arrowTip.value
              ) : props.tipAppearance?.shape === 'tie' ? (
                tieTip.value
              ) : (
                <div
                  class={tipClass({})}
                  style={[tipStyle.value, unref(props.disabled) ? { display: 'none' } : {}]}
                />
              )}
            </div>
          </div>
        </Teleport>
      )
    }
  }
})

Bubbletip.props = [
  'text',
  'placement',
  'disabled',
  'customClass',
  'body',
  'variant',
  'size',
  'triggerRef',
  'visible',
  'noMouseEvents',
  'spacing',
  'hoverable',
  'ignore',
  'active',
  'outputVisibility',
  'noPadding',
  'targetPosition',
  'referencePoint',
  'tipAppearance',
  'hoverableDelay',
  'hook',
  'zIndex',
  'wrapText',
  'style',
  'eager'
]

export default Bubbletip
